import React, { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react';
import { BasicModalV2, TextButton } from '../../../../components/index.js';
import CustomModalHeader from '../../../Common/customComponents/CustomModalHeader.tsx';

import _ from 'lodash';
import UploadIcon from '/assets/icon/global/upload_file_upload.svg';
import ExtIcon from '/assets/icon/global/upload_file.svg';
import { v4 as uuidv4 } from 'uuid';

interface IProps {
  setIsModalOpen: Dispatch<SetStateAction<boolean>>;
  isModalOpen: boolean;
  title: string;
  onSaveFiles: (files: UploadFile[]) => void;
}

interface UploadFile {
  fileId: string;
  fileOriginalname: string;
  file: File;
  progress: number;
  size: number;
  isNewFile?: boolean;
}

const formatFileSize = (size: number) => {
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
  if (size < 1024 * 1024 * 1024) return `${(size / (1024 * 1024)).toFixed(1)} MB`;
  return `${(size / (1024 * 1024 * 1024)).toFixed(1)} GB`;
};

const PermitIssueTypeModal = (props: IProps) => {
  const [files, setFiles] = useState<UploadFile[]>([]);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [data, setData] = useState<any>({});

  useEffect(() => {
    if (!props.isModalOpen) {
      setFiles([]);
    }
  }, [props.isModalOpen]);

  const onFilesAdded = (newFiles: FileList | null) => {
    if (!newFiles) return;

    const addedFiles: UploadFile[] = Array.from(newFiles).map((file) => ({
      fileId: uuidv4(),
      fileOriginalname: file.name,
      file,
      progress: 0,
      size: file.size,
      isNewFile: true
    }));

    setFiles((prev) => [...prev, ...addedFiles]);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    onFilesAdded(e.dataTransfer.files);
  };

  const handleSave = async () => {
    setFiles((prev) =>
      prev.map((f) => ({
        ...f,
        status: 'uploading',
        progress: 0
      }))
    );

    props.onSaveFiles(files);

    await simulateProgress();

    props.setIsModalOpen(false);
  };

  const simulateProgress = async () => {
    for (const item of files) {
      await new Promise<void>((resolve) => {
        let current = 0;

        const interval = setInterval(() => {
          current += 10;

          setFiles((prev) =>
            prev.map((f) =>
              f.fileId === item.fileId
                ? {
                    ...f,
                    progress: Math.min(current, 100),
                    size: (f.file.size * current) / 100,
                    status: current >= 100 ? 'done' : 'uploading'
                  }
                : f
            )
          );

          if (current >= 100) {
            clearInterval(interval);
            resolve();
          }
        }, 200);
      });
    }
  };

  const handleChooseClick = () => {
    inputRef.current?.click();
  };
  const modalClose = () => {
    props.setIsModalOpen(false);
  };

  return (
    <BasicModalV2 open={props.isModalOpen} onClose={() => props.setIsModalOpen(false)}>
      <CustomModalHeader title={props.title} modalClose={modalClose} />
      <div className="custom-modal-body file-form-modal" style={{ width: '810px', height: '100%' }}>
        <div className="contents-box uploader-root">
          <div className="attach-description">Attach the file below</div>
          <div
            className="drag-drop-area"
            onDragOver={(e) => e.preventDefault()}
            onDrop={handleDrop}
            onClick={handleChooseClick}
          >
            <div className="drag-content">
              <img src={UploadIcon} alt="upload icon" className="upload-icon" />
              <span className="drag-text">
                Drag & Drop or <span className="choose-text">Choose files</span> to upload
              </span>
            </div>

            <input
              ref={inputRef}
              type="file"
              multiple
              style={{ display: 'none' }}
              onChange={(e) => onFilesAdded(e.target.files)}
            />
          </div>

          {files.length > 0 && (
            <div className="file-list">
              {files.map((item) => (
                <div className="file-data" key={item.fileId}>
                  <img src={ExtIcon} className="file-icon" alt="file icon" />
                  <div className="file-info">
                    <div className="file-data-name">{item.file.name}</div>
                    <div className="file-size">
                      {formatFileSize(item.size)} of {formatFileSize(item.file.size)}
                    </div>
                    <div className="file-progress">
                      <div className="file-progress-bar" style={{ width: `${item.progress}%` }} />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
          <div className="button-box modal-footer">
            <TextButton
              className="cancel-style"
              variant="outlined"
              style={{ height: '40px' }}
              onClick={modalClose}
            >
              Close
            </TextButton>

            <TextButton variant="contained" style={{ height: '40px' }} onClick={handleSave}>
              Save
            </TextButton>
          </div>
        </div>
      </div>
    </BasicModalV2>
  );
};

export default PermitIssueTypeModal;
